/*
 * Copyright (c) 2024, King Living
 * View details block below the configuration gallery (thumbnails, title, short + long description).
 *
 * Swiper column is fixed 254px (lg). Remaining width is shared by the two text columns in
 * `contentPair` (flex-grow ratio PAIR_TITLE_FLEX : PAIR_LONG_FLEX).
 */

const PAIR_REF_WIDTH_PX = 570;
const PAIR_TITLE_PX = 180;
const PAIR_GAP_PX = 24;
const PAIR_LONG_PX = 366;

const PAIR_TITLE_PCT = `${(PAIR_TITLE_PX / PAIR_REF_WIDTH_PX) * 100}%`;
const PAIR_LONG_PCT = `${(PAIR_LONG_PX / PAIR_REF_WIDTH_PX) * 100}%`;

const PAIR_TITLE_WIDTH_CLAMP = `clamp(${PAIR_TITLE_PX}px, ${PAIR_TITLE_PCT}, 100%)`;
const PAIR_LONG_WIDTH_CLAMP = `clamp(${PAIR_LONG_PX}px, ${PAIR_LONG_PCT}, 100%)`;

/** lg media — must match theme breakpoints `lg` */
const LG_MEDIA = '@media (min-width: 992px)';

/** Fixed desktop width for the thumbnail swiper column */
const THUMB_SWIPER_WIDTH = '254px';

const ViewDetails = {
  parts: [
    'root',
    'contentPair',
    'thumbnailSwiperWrapper',
    'thumbnailSlide',
    'thumbnailSlideHit',
    'thumbnailZoomIconBadge',
    'thumbnailZoomIcon',
    'thumbnailImage',
    'configNameColumn',
    'title',
    'description',
    'longDescriptionSkeleton',
    'longDescription',
    'configurationName',
    'shortDescription',
    'descriptionWrapper',
    'descriptionWrapperSkeleton',
    'placeholderWrapper',
    'placeholderImage',
  ],

  baseStyle: {
    root: {
      alignItems: {base: 'stretch', lg: 'flex-start'},
      display: 'flex',
      flexDirection: {base: 'column', lg: 'row'},
      flexWrap: {base: 'nowrap', lg: 'nowrap'},
      gap: {base: 6, lg: '28px'},
      paddingBottom: {base: 10, lg: 4},
      paddingTop: 0,
      paddingX: {base: 5, lg: 0},
      marginLeft: {base: 0, lg: -3},
    },
    contentPair: {
      alignItems: {base: 'stretch', lg: 'flex-start'},
      display: 'flex',
      flex: {base: 'none', lg: '1 1 0%'},
      flexDirection: {base: 'column', lg: 'row'},
      gap: {base: 4, lg: `${PAIR_GAP_PX}px`},
      minW: {base: 0, lg: 0},
      width: {base: '100%', lg: 'auto'},
      sx: {
        '&.view-details-content-pair--no-thumbnail': {
          paddingLeft: {lg: '12px'},
        },
      },
    },
    thumbnailSwiperWrapper: {
      flex: {base: 'none', lg: `0 0 ${THUMB_SWIPER_WIDTH}`},
      marginLeft: {base: '-12px', lg: 0},
      marginTop: {base: 2, lg: 0},
      maxW: {base: 'unset', lg: THUMB_SWIPER_WIDTH},
      minW: {base: 0, lg: THUMB_SWIPER_WIDTH},
      order: {base: 2, lg: 0},
      overflow: 'visible',
      width: {base: 'calc(100% + 28px)', lg: THUMB_SWIPER_WIDTH},
      sx: {
        '.swiper-pagination.swiper-pagination-bullets': {
          backdropFilter: 'unset',
          backgroundColor: 'transparent',
          bottom: 0,
          display: {base: 'none', lg: 'flex'},
          gap: '6px',
        },
      },
    },
    thumbnailSlide: {
      borderRadius: 'md',
      display: 'flex',
      overflow: 'hidden',
      position: 'relative',
      width: '100%',
      sx: {
        '.view-details-thumbnail-slide-content img': {
          objectFit: 'contain',
        },
      },
    },
    thumbnailSlideHit: {
      bg: 'transparent',
      border: 'none',
      cursor: 'pointer',
      display: 'block',
      flex: 1,
      minH: 0,
      p: 0,
      position: 'relative',
      textAlign: 'left',
      w: '100%',
      aspectRatio: '230 / 156',
    },
    thumbnailZoomIconBadge: {
      alignItems: 'center',
      bg: 'transparent',
      border: 'none',
      bottom: 'unset',
      display: 'flex',
      justifyContent: 'center',
      p: 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: 2,
      top: '8px',
      zIndex: 1,
    },
    thumbnailZoomIcon: {
      color: 'brandColors.greyscale80',
      h: 4.75,
      stroke: 'brandColors.greyscale80',
      strokeWidth: '1px',
      w: 4.75,
    },
    thumbnailImage: {
      height: '100%',
      objectFit: 'contain',
      width: '100%',
    },
    configNameColumn: {
      display: 'flex',
      flexDirection: 'column',
      flex: {base: 'none', lg: '0 1 auto'},
      gap: {base: 1.5, lg: 3},
      maxW: {base: '100%', lg: '100%'},
      minW: {base: 0, lg: 0},
      overflow: {lg: 'hidden'},
      width: {
        base: '100%',
        lg: PAIR_TITLE_WIDTH_CLAMP,
      },
      sx: {
        [LG_MEDIA]: {
          '&:only-child': {
            flex: '1 1 auto',
            maxW: '100%',
            width: '100%',
          },
        },
      },
    },
    title: {
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontSize: {base: 'xl', lg: 'lg'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 500,
      lineHeight: {base: 1.5, lg: 1.2},
    },
    description: {
      color: 'brandColors.greyscale70',
      fontSize: {base: 'sm', lg: 'md'},
      sx: {
        '& *': {
          color: 'brandColors.greyscale70',
          fontSize: {base: 'sm', lg: 'md'},
        },
        '& > *': {
          color: 'brandColors.greyscale70',
          fontSize: {base: 'sm', lg: 'md'},
        },
        '& .amb-markup p': {
          color: 'brandColors.greyscale70',
        },
        '& ul, & ol': {
          paddingLeft: {base: 2, lg: 5},
        },
        '& a': {
          color: 'primary.darkLinen',
          fontSize: {base: '0.8125rem', lg: '0.75rem'},
          fontWeight: 600,
        },
      },
    },
    longDescriptionSkeleton: {
      flex: {base: 'none', lg: '0 1 auto'},
      marginLeft: {base: 0, lg: 0},
      minW: {base: 0, lg: 0},
      width: {
        base: '100%',
        lg: PAIR_LONG_WIDTH_CLAMP,
      },
    },
    longDescription: {
      color: 'brandColors.greyscale90',
      flex: {base: 'none', lg: '1 1 auto'},
      fontFamily: 'body',
      fontSize: {base: 'sm', lg: 'md'},
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 1.4,
      minW: {base: 0, lg: 0},
      width: {base: '100%', lg: '100%'},
      sx: {
        '& *': {
          color: 'brandColors.greyscale90',
          fontSize: {base: 'sm', lg: 'md'},
        },
        '& .amb-markup p': {
          color: 'brandColors.greyscale90',
        },
        '& ul, & ol': {
          paddingLeft: {base: 2, lg: 5},
        },
        '& a': {
          color: 'primary.darkLinen',
          fontSize: {base: '0.8125rem', lg: '0.75rem'},
          fontWeight: 600,
        },
      },
    },
    placeholderWrapper: {
      alignItems: 'center',
      aspectRatio: 256 / 123,
      bg: 'primary.paleLinen',
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
    },
    placeholderImage: {
      height: 'auto',
      maxHeight: '75%',
      maxWidth: '75%',
      objectFit: 'contain',
      userSelect: 'none',
    },
    descriptionWrapper: {
      color: 'brandColors.greyscale70',
      fontFamily: 'body',
      fontSize: {base: 'sm', lg: 'md'},
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 1.4,
      sx: {
        '& *': {
          color: 'brandColors.greyscale70',
          fontSize: {base: 'sm', lg: 'md'},
        },
        '& .amb-markup p': {
          color: 'brandColors.greyscale70',
        },
        '& ul, & ol': {
          paddingLeft: {base: 2, lg: 5},
        },
      },
    },
  },
};

export default ViewDetails;
